import React, { useEffect, useContext, useState } from 'react';
import clsx from 'clsx';

import { cloneDeep } from 'lodash';

import {
    Grid,
    Typography,
    TextField,
    Button,
    FormControl,
    FormHelperText,
    CircularProgress
} from '@material-ui/core';

import {
    UploadFile
} from '../../../../common';

import { ToastContext } from '../../../../contexts/ToastContext';
import { PageContext } from '../../PageContext';

import { FileType } from '../../../../../helpers';

import { useTranslation } from "react-i18next";

import ClearIcon from '@material-ui/icons/Clear';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import HttpsIcon from '@material-ui/icons/Https';

import { makeStyles } from '@material-ui/core/styles';
const useStyles = makeStyles((theme) => ({
    root: {
    },
    navButton: {
        margin: theme.spacing(1),
    },
    progress: {
        marginRight: theme.spacing(1)
    }
}));

const MAX_STEPS = 1;

export const GoogleCertificatesWizard = ({
    disabled,
    upload,
    onWizardComplete,
    onWizardCanceled,
    ...props }) => {
    const classes = useStyles();

    const { t, i18n } = useTranslation();

    const {
        state,
        isBusy
    } = useContext(PageContext);

    const [wizardValues, setWizardValues] = useState({
        googleUserEmail: { value: "", isValid: true, errorText: null },
        keystoreAlias: { value: "", isValid: true, errorText: null }
    });

    const {
        showSuccessMessage,
        showWarningMessage
    } = useContext(ToastContext);

    const {
        settings
    } = state;

    const validateWizardData = () => {
        let newValues = null;
        switch (currentStep) {
            case 0:
                newValues = {
                    googleUserEmail: checkWizardValue("googleUserEmail"),
                    keystoreAlias: checkWizardValue("keystoreAlias"),
                };
                break;

            default:
                break;
        }

        if (newValues) {
            let isValid = true;
            Object.keys(newValues).forEach((key) => {
                if (!newValues[key].isValid)
                    isValid = false;
            });
            setWizardValues({
                ...cloneDeep(wizardValues),
                ...newValues
            });
            return isValid;
        } else {
            return false;
        }
    }

    const setWizardValue = (value, isValid, errorText) => {
        return { value, isValid: isValid ?? true, errorText: errorText ?? null }
    }

    const checkWizardValue = (valueName) => {
        return setWizardValue(
            wizardValues[valueName].value,
            wizardValues[valueName].value !== "",
            wizardValues[valueName].value === "" ? t("YupValueRequired") : null
        )
    }

    const [currentStep, setStep] = useState(0);

    const stepBackClickHandler = (e) => {
        if (currentStep > 0)
            setStep(currentStep - 1);
    }

    const stepNextClickHandler = (e) => {
        if (!validateWizardData()) return;
        if (currentStep < MAX_STEPS - 1)
            setStep(currentStep + 1);
    }

    const canceledClickHandler = (e) => {
        if (onWizardCanceled) onWizardCanceled();
    }

    const completeClickHandler = (e) => {
        if (!validateWizardData()) return;
        if (onWizardComplete) {
            onWizardComplete(
                wizardValues.keystoreAlias.value,
                wizardValues.googleUserEmail.value,
                settings.bundleId
            );
        }
    }

    return (
        <Grid container spacing={3} className={clsx(classes.root, props.className)}>
            <Grid item xs={12}>
                <Typography variant="h5">
                    {t("LabelCertWizardTitle")}
                </Typography>
                <Typography variant="subtitle1">
                    {t("LabelStep")} {currentStep + 1} {t("LabelOf")} {MAX_STEPS}
                </Typography>
            </Grid>
            {currentStep === 0 &&
                <>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            error={!wizardValues.googleUserEmail.isValid}
                            helperText={wizardValues.googleUserEmail.errorText}
                            type="email"
                            variant="outlined"
                            label="Google Developer Email"
                            disabled={disabled}
                            inputProps={{
                                autoComplete: 'new-email',
                                form: {
                                    autoComplete: 'off'
                                }
                            }}
                            value={wizardValues.googleUserEmail.value}
                            onChange={(e) => setWizardValues({ ...cloneDeep(wizardValues), googleUserEmail: setWizardValue(e.target.value) })}
                        />
                        <Typography variant="caption" component="div" style={{ marginTop: 3, marginLeft: 14, color: "#515151" }}>
                            {t("HelperAndroidDevEmail")}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            error={!wizardValues.keystoreAlias.isValid}
                            helperText={wizardValues.keystoreAlias.errorText}
                            variant="outlined"
                            label="Keystore Alias"
                            disabled={disabled}
                            value={wizardValues.keystoreAlias.value}
                            onChange={(e) => setWizardValues({ ...cloneDeep(wizardValues), keystoreAlias: setWizardValue(e.target.value) })}
                        />
                        <Typography variant="caption" component="div" style={{ marginTop: 3, marginLeft: 16, color: "#515151" }}>
                            {t("HelperAndroidDevKSAlias")}
                        </Typography>
                    </Grid>
                </>
            }
            <Grid container item justify="space-between">
                {currentStep === 0 &&
                    <Button
                        variant="contained"
                        startIcon={<ClearIcon />}
                        disabled={disabled}
                        onClick={canceledClickHandler}>
                        {t("ButtonCancel")}
                    </Button>
                }
                {currentStep > 0 &&
                    <Button
                        variant="contained"
                        startIcon={<NavigateBeforeIcon />}
                        disabled={disabled || currentStep == 0}
                        onClick={stepBackClickHandler}>
                        {t("ButtonBack")}
                    </Button>
                }
                {currentStep < MAX_STEPS - 1 &&
                    <Button
                        variant="contained"
                        endIcon={<NavigateNextIcon />}
                        disabled={disabled || currentStep == MAX_STEPS - 1}
                        onClick={stepNextClickHandler}>
                        {t("ButtonNext")}
                    </Button>
                }
                {currentStep === MAX_STEPS - 1 &&
                    <Button
                        variant="contained"
                        disabled={disabled}
                        onClick={completeClickHandler}>
                        {isBusy("creatingCerts") &&
                            <CircularProgress className={classes.progress} size={20} />
                        }
                        {t("ButtonGenerate")}
                    </Button>
                }
            </Grid>
        </Grid>
    );
}